import React, { useState } from 'react';
import cx from 'classnames';
import { Button } from '@THC/components/Button';
import { MDText } from 'i18n-react';

import cypressIds from 'Components/cypress_ids.json';
import { FeaturesRetailValue } from 'Components/pages/VehicleDetailsRedesign/sections/VehicleFeaturesSection/FeaturesRetailValue/FeaturesRetailValue';
import IconList from 'Components/shared/IconList/IconList';
import type { VehicleFeatures } from 'Pages/vehicles/helpers/vehicle.schema';
import { triggerFeaturesModalClosedEvent, triggerFeaturesViewAllLinkClickedEvent } from 'Services/analytics/events';
import type { Vehicle } from 'Types/vehicle';
import { applyCypressData } from 'Utilities/helpers';

import { FeaturesModal } from '../FeaturesModal/FeaturesModal';
import vehicleFeaturesSectionTexts from '../VehicleFeaturesSection.texts';

import { getFeatures } from './FeaturesList.helpers';

import styles from './FeaturesList.module.scss';

const LocalT = new MDText(vehicleFeaturesSectionTexts.list);

interface FeaturesListProps {
	classNames: {
		container?: string;
		list?: string;
	};
	equipment: Vehicle['equipment'];
	highValueCarFeatures: VehicleFeatures['highValueCarFeatures'];
}

export const FeaturesList: React.FC<FeaturesListProps> = ({ classNames = {}, equipment, highValueCarFeatures }) => {
	const [showModal, setShowModal] = useState(false);
	const shouldShowRetailValue = Boolean(highValueCarFeatures.items.length && highValueCarFeatures?.totalCost);
	const equipmentItems = equipment.split(',');

	const { featuresCount, hiddenFeatures, visibleFeatures } = getFeatures({
		equipmentItems,
		highValueCarFeaturesItems: highValueCarFeatures.items,
	});

	const onViewAllLinkClick = () => {
		setShowModal(true);
		triggerFeaturesViewAllLinkClickedEvent();
	};

	const onModalDismiss = () => {
		setShowModal(false);
		triggerFeaturesModalClosedEvent();
	};

	return (
		<div className={cx(classNames.list, classNames.container)}>
			{shouldShowRetailValue && (
				<div className={styles.retailValue}>
					<FeaturesRetailValue highValueCarFeatures={highValueCarFeatures} />
				</div>
			)}
			<IconList items={visibleFeatures} />
			{hiddenFeatures.length > 0 && (
				<div className={styles.viewAll}>
					<Button
						{...applyCypressData(cypressIds.buttons.featuresViewAll)}
						label={LocalT.translate('viewAll', { count: featuresCount })}
						onClick={() => onViewAllLinkClick()}
						size="small"
						variant="tertiary"
					/>
				</div>
			)}
			{showModal && (
				<FeaturesModal
					equipment={equipment}
					highValueCarFeatures={highValueCarFeatures}
					modalHandler={onModalDismiss}
				/>
			)}
		</div>
	);
};
